import styled, { css } from 'styled-components';
import { useNavigate } from '@avangard/utils/routing';
import { white } from '@avangard/ui/colors';
import { breakpoints, isRtl } from '@avangard/ui/utils';

import { routes } from '@config/routes';
import {
    studentOnlineApplicationBackground,
    studentOnlineApplicationLogo,
} from '@modules/auth/helpers/assets/images';

type StudentOnlineApplicationCardProps = {
    deadline: string;
};

const StudentOnlineApplicationCard = (props: StudentOnlineApplicationCardProps) => {
    const { deadline } = props;

    const navigate = useNavigate();

    const handleClick = async () => navigate(routes.studentOnlineApplication.path);

    return (
        <Root background={studentOnlineApplicationBackground} onClick={handleClick}>
            <Logo>
                <img src={studentOnlineApplicationLogo} alt='student-online-application-logo' />
            </Logo>

            <Main>
                <Title>Анкета перед сборами</Title>
                <Content dangerouslySetInnerHTML={{ __html: deadline }} />
            </Main>
        </Root>
    );
};

const Logo = styled.div`
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    align-items: center;

    img {
        object-fit: contain;
        height: 100%;
    }
`;

const Main = styled.div`
    display: flex;
    flex-wrap: wrap;
    max-width: calc(100% - 74px);
    margin-left: 24px;

    color: ${white[100]};
`;

const Title = styled.div`
    width: 100%;
    font-size: 1.4rem;
    font-weight: 700;
    text-transform: uppercase;
`;

const Content = styled.div`
    margin-top: 4px;
    width: 100%;
    font-size: 1.4rem;

    color: rgba(255, 255, 255, 0.5);
`;

const Root = styled.div<{ background: string; disabled?: boolean }>`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    position: relative;
    max-width: 916px;
    width: 100%;
    margin-bottom: 32px;
    padding: 20px 24px;
    border-radius: 5px;
    overflow: hidden;
    cursor: pointer;
    transition: 0.2s ease-in-out;
    transform: scale(1);

    &:before,
    &:after {
        content: '';
        display: block;
        position: absolute;
        z-index: -1;
        left: 0;
        top: 0;
        height: 100%;
        width: 100%;
        pointer-events: none;

        ${isRtl`
            left: initial;
            right: 0;
        `}
    }

    &:before {
        background-image: ${p => `url(${p.background})`};
    }

    &:after {
        background-color: rgba(255, 138, 0, 0.5);
    }

    &:hover {
        transform: scale(1.05);
        filter: brightness(130%);
    }

    ${p =>
        !!p.disabled &&
        css`
            opacity: 0.8;
            cursor: default;
            pointer-events: none;

            &:before {
                filter: grayscale(100%) brightness(50%);
            }
        `}

    ${breakpoints.down(1600)} {
        max-width: 736px;
    }

    ${breakpoints.down(752)} {
        justify-content: center;
        max-width: 360px;
        margin-bottom: 10px;
        padding: 16px;
    }

    ${breakpoints.down(600)} {
        max-width: 320px;
        margin: 12px 0;
        padding: 24px;

        text-align: center;

        &:before {
            background-size: cover;
            background-position: center;
        }

        > * {
            max-width: 100%;
            width: 100%;
        }

        ${Logo} {
            padding-bottom: 12px;
        }

        ${Title} {
            padding-bottom: 12px;
        }

        ${Main} {
            margin-left: 0;
        }
    }
`;

export { StudentOnlineApplicationCard };
